import { useState, useEffect, useRef } from "react";
import { X, Search, User, Loader, ArrowRight, ChevronDown } from "lucide-react";

const BOARD_COLS = [
  { id: "todo",       label: "To do",       color: "#f59e0b", apiStatus: "TODO" },
  { id: "inprogress", label: "In Progress", color: "#009DB9", apiStatus: "IN_PROGRESS" },
  { id: "completed",  label: "Completed",   color: "#10b981", apiStatus: "COMPLETED" },
];

const PRIORITY_CHIP = {
  HIGH: { bg: "#fef2f2", color: "#dc2626", label: "High", order: 1 },
  MEDIUM: { bg: "#fefce8", color: "#ca8a04", label: "Medium", order: 2 },
  LOW: { bg: "#f0fdf4", color: "#16a34a", label: "Low", order: 3 },
};

// Role-based permissions
const CAN_ASSIGN_TO_OTHERS = ["SUPER_ADMIN", "ADMIN", "MANAGER"];

/* ── Shared modal wrapper ── */
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col w-full max-w-md mx-4 bg-white shadow-2xl rounded-2xl md:mx-0"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 border-b border-neutral-100 md:px-5 md:py-4">
          <span className="text-[14px] font-bold text-neutral-900">{title}</span>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
            <X size={15} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

/* ── Simple Dropdown Component ── */
function SimpleDropdown({ label, required, placeholder, value, onChange, options, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option => 
    option.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-1" ref={dropdownRef}>
      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`
            flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer transition-all
            ${disabled ? 'bg-neutral-50 cursor-not-allowed opacity-60' : 'bg-white hover:border-[#009DB9]'}
            ${isOpen ? 'border-[#009DB9]' : 'border-neutral-200'}
          `}
        >
          <span className={`text-[13px] ${value ? 'text-neutral-900' : 'text-neutral-400'}`}>
            {value || placeholder}
          </span>
          <ChevronDown size={16} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        {isOpen && !disabled && (
          <div className="absolute left-0 right-0 z-20 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-neutral-100">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-1.5 text-[13px] border border-neutral-200 rounded-md focus:outline-none focus:border-[#009DB9]"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      onChange(option);
                      setSearchTerm("");
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-[13px] text-neutral-800 hover:bg-neutral-50 transition-colors border-b border-neutral-50 last:border-0"
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className="px-3 py-8 text-center text-[12px] text-neutral-400">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Add Task Modal ── */
export function AddTaskModal({ open, onClose, onAdd, contacts, deals, users, currentUser }) {
  const [form, setForm] = useState({ 
    title: "", 
    contactId: "", 
    dealId: "", 
    assignedTo: "",
    dueDate: "", 
    priority: "MEDIUM", 
    status: "TODO", 
    description: "" 
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const userRole = currentUser?.role || "SALES_REP";
  const canAssignToOthers = CAN_ASSIGN_TO_OTHERS.includes(userRole);
  
  // Auto-assign to current user if they can't assign to others
  useEffect(() => {
    if (!canAssignToOthers && currentUser?.id && open) {
      setForm(prev => ({ ...prev, assignedTo: currentUser.id }));
    }
  }, [canAssignToOthers, currentUser, open]);
  
  // Company options
  const companyOptions = contacts.map(c => ({
    id: c.id,
    label: c.company,
    contact: c
  }));
  
  // Deal options - filtered by selected company
  const dealOptions = deals
    .filter(deal => selectedCompany ? deal.contactId === selectedCompany.id : false)
    .map(deal => ({
      id: deal.id,
      label: deal.title
    }));
  
  // User options - only show if user can assign to others
  const userOptions = canAssignToOthers
    ? users?.map(u => ({
        id: u.id,
        label: `${u.firstName} ${u.lastName}`
      })) || []
    : [];

  const handleCompanySelect = (option) => {
    setSelectedCompany(option.contact);
    setForm(prev => ({ ...prev, contactId: option.id, dealId: "" }));
  };

  const handleDealSelect = (option) => {
    setForm(prev => ({ ...prev, dealId: option.id }));
  };

  const handleUserSelect = (option) => {
    setForm(prev => ({ ...prev, assignedTo: option.id }));
  };

  const handleAdd = async () => {
    if (!form.title || !form.contactId) {
      alert("Please fill in task title and select a company");
      return;
    }
    
    setLoading(true);
    try {
      const taskData = {
        title: form.title,
        description: form.description,
        dueDate: form.dueDate,
        priority: form.priority,
        status: form.status,
        contactId: form.contactId,
        dealId: form.dealId || null,
        assignedTo: form.assignedTo || null,
      };
      
      await onAdd(taskData);
      setForm({ title: "", contactId: "", dealId: "", assignedTo: "", dueDate: "", priority: "MEDIUM", status: "TODO", description: "" });
      setSelectedCompany(null);
      onClose();
    } catch (error) {
      console.error("Error adding task:", error);
      alert(error.response?.data?.message || "Failed to add task");
    } finally {
      setLoading(false);
    }
  };

  const selectedCompanyName = selectedCompany?.company;
  const selectedDealName = deals.find(d => d.id === form.dealId)?.title;
  const contactPersonName = selectedCompany?.fullName || (selectedCompany ? `${selectedCompany.firstName} ${selectedCompany.lastName}` : '');

  return (
    <Modal open={open} onClose={onClose} title="Add Task">
      <div className="flex flex-col gap-3 px-4 py-4 md:px-5">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Task Title *</label>
          <input 
            value={form.title} 
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. Send proposal PDF"
            className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors" 
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SimpleDropdown
            label="Company"
            required
            placeholder="Select a company..."
            value={selectedCompanyName}
            onChange={handleCompanySelect}
            options={companyOptions}
          />
          
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Due Date</label>
            <input 
              type="date" 
              value={form.dueDate} 
              onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
              className="h-9 border border-neutral-200 rounded-lg px-2 text-[12px] outline-none focus:border-[#009DB9] transition-colors" 
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Contact Person</label>
          <input
            type="text"
            value={contactPersonName}
            readOnly
            placeholder="Select a company to auto-fill contact person"
            className="h-9 border border-neutral-200 rounded-lg px-3 text-[12px] bg-neutral-50 text-neutral-600 cursor-not-allowed"
            style={{ background: "#f9fafb" }}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SimpleDropdown
            label="Deal (Optional)"
            placeholder={selectedCompany ? "Select a deal..." : "Select a company first"}
            value={selectedDealName}
            onChange={handleDealSelect}
            options={dealOptions}
            disabled={!selectedCompany}
          />
          
          {canAssignToOthers ? (
            <SimpleDropdown
              label="Assigned To"
              placeholder="Select a user..."
              value={userOptions.find(u => u.id === form.assignedTo)?.label}
              onChange={handleUserSelect}
              options={userOptions}
              disabled={false}
            />
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Assigned To</label>
              <div className="px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-500 text-[13px]">
                Cannot assign tasks
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Priority</label>
          <div className="flex gap-2">
            {Object.entries(PRIORITY_CHIP).map(([key, val]) => (
              <button 
                key={key} 
                onClick={() => setForm(prev => ({ ...prev, priority: key }))}
                className="flex-1 py-1.5 rounded-lg text-[11px] sm:text-[12px] font-semibold border transition-all"
                style={form.priority === key
                  ? { background: val.bg, color: val.color, borderColor: val.color + "60" }
                  : { background: "#fff", color: "#737373", borderColor: "#e5e5e5" }}
              >
                {val.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Status</label>
          <div className="flex gap-2">
            {BOARD_COLS.map((col) => (
              <button 
                key={col.id} 
                onClick={() => setForm(prev => ({ ...prev, status: col.apiStatus }))}
                className="flex-1 py-1.5 rounded-lg text-[10px] sm:text-[11px] font-semibold border transition-all"
                style={form.status === col.apiStatus
                  ? { background: `${col.color}18`, color: col.color, borderColor: `${col.color}50` }
                  : { background: "#fff", color: "#737373", borderColor: "#e5e5e5" }}
              >
                {col.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Description</label>
          <textarea 
            rows={2} 
            value={form.description} 
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Any additional context..."
            className="border border-neutral-200 rounded-lg px-3 py-2 text-[12px] outline-none focus:border-[#009DB9] resize-none transition-colors" 
          />
        </div>
      </div>
      
      <div className="flex flex-col-reverse gap-2 px-4 py-4 border-t border-neutral-100 sm:flex-row sm:justify-end md:px-5">
        <button 
          onClick={onClose} 
          className="px-4 py-2 rounded-lg text-[12px] font-medium text-neutral-600 border border-neutral-200 hover:bg-neutral-50 sm:py-1.5"
        >
          Cancel
        </button>
        <button 
          onClick={handleAdd} 
          disabled={!form.title || !form.contactId || loading}
          className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2 sm:py-1.5"
          style={{ background: "#009DB9" }}
        >
          {loading ? <Loader size={14} className="animate-spin" /> : "Add Task"}
        </button>
      </div>
    </Modal>
  );
}

/* ── Edit Task Modal ── */
export function EditTaskModal({ open, onClose, task, onUpdate, contacts, deals, users, currentUser }) {
  const [form, setForm] = useState({ 
    title: "", 
    contactId: "", 
    dealId: "", 
    assignedTo: "",
    dueDate: "", 
    priority: "MEDIUM", 
    status: "TODO", 
    description: "" 
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const userRole = currentUser?.role || "SALES_REP";
  const canAssignToOthers = CAN_ASSIGN_TO_OTHERS.includes(userRole);
  
  useEffect(() => {
    if (task && contacts.length > 0) {
      const companyContact = contacts.find(c => c.id === task.contactId);
      setForm({
        title: task.title || "",
        contactId: task.contactId || "",
        dealId: task.dealId || "",
        assignedTo: task.assignedTo || "",
        dueDate: task.dueDate?.split('T')[0] || "",
        priority: task.priority || "MEDIUM",
        status: task.status || "TODO",
        description: task.description || ""
      });
      if (companyContact) {
        setSelectedCompany(companyContact);
      }
      
      // For non-admin users, force assign to themselves
      if (!canAssignToOthers && currentUser?.id) {
        setForm(prev => ({ ...prev, assignedTo: currentUser.id }));
      }
    }
  }, [task, contacts, canAssignToOthers, currentUser]);
  
  const companyOptions = contacts.map(c => ({
    id: c.id,
    label: c.company,
    contact: c
  }));
  
  const dealOptions = deals
    .filter(deal => selectedCompany ? deal.contactId === selectedCompany.id : false)
    .map(deal => ({
      id: deal.id,
      label: deal.title
    }));
  
  const userOptions = canAssignToOthers
    ? users?.map(u => ({
        id: u.id,
        label: `${u.firstName} ${u.lastName}`
      })) || []
    : [];

  const handleCompanySelect = (option) => {
    setSelectedCompany(option.contact);
    setForm(prev => ({ ...prev, contactId: option.id, dealId: "" }));
  };

  const handleDealSelect = (option) => {
    setForm(prev => ({ ...prev, dealId: option.id }));
  };

  const handleUserSelect = (option) => {
    setForm(prev => ({ ...prev, assignedTo: option.id }));
  };

  const handleUpdate = async () => {
    if (!form.title || !task) return;
    
    setLoading(true);
    try {
      const taskData = {
        title: form.title,
        description: form.description,
        dueDate: form.dueDate,
        priority: form.priority,
        status: form.status,
        contactId: form.contactId || null,
        dealId: form.dealId || null,
        assignedTo: form.assignedTo || null,
      };
      
      await onUpdate(task.id, taskData);
      onClose();
    } catch (error) {
      console.error("Error updating task:", error);
      alert(error.response?.data?.message || "Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  const selectedCompanyName = selectedCompany?.company;
  const selectedDealName = deals.find(d => d.id === form.dealId)?.title;

  return (
    <Modal open={open} onClose={onClose} title="Edit Task">
      <div className="flex flex-col gap-3 px-4 py-4 md:px-5">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Task Title *</label>
          <input 
            value={form.title} 
            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. Send proposal PDF"
            className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors" 
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SimpleDropdown
            label="Company"
            required
            placeholder="Select a company..."
            value={selectedCompanyName}
            onChange={handleCompanySelect}
            options={companyOptions}
          />
          
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Due Date</label>
            <input 
              type="date" 
              value={form.dueDate} 
              onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
              className="h-9 border border-neutral-200 rounded-lg px-2 text-[12px] outline-none focus:border-[#009DB9] transition-colors" 
            />
          </div>
        </div>

        {selectedCompany && (
          <div className="p-3 border border-blue-100 rounded-lg bg-blue-50">
            <label className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1 block">Contact Person</label>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                style={{ background: "#009DB9" }}>
                {selectedCompany.fullName?.charAt(0) || selectedCompany.firstName?.charAt(0) || "?"}
              </div>
              <div>
                <div className="text-[13px] font-semibold text-neutral-800">
                  {selectedCompany.fullName || `${selectedCompany.firstName} ${selectedCompany.lastName}`}
                </div>
                <div className="text-[11px] text-neutral-500">
                  {selectedCompany.email} • {selectedCompany.phone || "No phone"}
                </div>
                {selectedCompany.jobTitle && (
                  <div className="text-[10px] text-neutral-400 mt-0.5">
                    {selectedCompany.jobTitle}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SimpleDropdown
            label="Deal (Optional)"
            placeholder={selectedCompany ? "Select a deal..." : "Select a company first"}
            value={selectedDealName}
            onChange={handleDealSelect}
            options={dealOptions}
            disabled={!selectedCompany}
          />
          
          {canAssignToOthers ? (
            <SimpleDropdown
              label="Assigned To"
              placeholder="Select a user..."
              value={userOptions.find(u => u.id === form.assignedTo)?.label}
              onChange={handleUserSelect}
              options={userOptions}
              disabled={false}
            />
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Assigned To</label>
              <div className="px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-500 text-[13px]">
                Cannot assign tasks - Task assigned to you
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Priority</label>
          <div className="flex gap-2">
            {Object.entries(PRIORITY_CHIP).map(([key, val]) => (
              <button 
                key={key} 
                onClick={() => setForm(prev => ({ ...prev, priority: key }))}
                className="flex-1 py-1.5 rounded-lg text-[11px] sm:text-[12px] font-semibold border transition-all"
                style={form.priority === key
                  ? { background: val.bg, color: val.color, borderColor: val.color + "60" }
                  : { background: "#fff", color: "#737373", borderColor: "#e5e5e5" }}
              >
                {val.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Status</label>
          <div className="flex gap-2">
            {BOARD_COLS.map((col) => (
              <button 
                key={col.id} 
                onClick={() => setForm(prev => ({ ...prev, status: col.apiStatus }))}
                className="flex-1 py-1.5 rounded-lg text-[10px] sm:text-[11px] font-semibold border transition-all"
                style={form.status === col.apiStatus
                  ? { background: `${col.color}18`, color: col.color, borderColor: `${col.color}50` }
                  : { background: "#fff", color: "#737373", borderColor: "#e5e5e5" }}
              >
                {col.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Description</label>
          <textarea 
            rows={2} 
            value={form.description} 
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Any additional context..."
            className="border border-neutral-200 rounded-lg px-3 py-2 text-[12px] outline-none focus:border-[#009DB9] resize-none transition-colors" 
          />
        </div>
      </div>
      
      <div className="flex flex-col-reverse gap-2 px-4 py-4 border-t border-neutral-100 sm:flex-row sm:justify-end md:px-5">
        <button 
          onClick={onClose} 
          className="px-4 py-2 rounded-lg text-[12px] font-medium text-neutral-600 border border-neutral-200 hover:bg-neutral-50 sm:py-1.5"
        >
          Cancel
        </button>
        <button 
          onClick={handleUpdate} 
          disabled={!form.title || !form.contactId || loading}
          className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2 sm:py-1.5"
          style={{ background: "#009DB9" }}
        >
          {loading ? <Loader size={14} className="animate-spin" /> : "Update Task"}
        </button>
      </div>
    </Modal>
  );
}

/* ── Move Task Modal ── */
export function MoveTaskModal({ open, onClose, task, onMove }) {
  const targets = BOARD_COLS.filter((c) => c.apiStatus !== task?.status);
  return (
    <Modal open={open} onClose={onClose} title="Move Task">
      <div className="flex flex-col gap-2 px-4 py-4 md:px-5">
        <p className="text-[12px] text-neutral-500 mb-1 line-clamp-2">{task?.title}</p>
        {targets.map((col) => (
          <button 
            key={col.id} 
            onClick={() => { onMove(col.apiStatus); onClose(); }}
            className="flex items-center gap-3 px-4 py-3 text-left transition-all border rounded-xl border-neutral-100 hover:border-neutral-200 hover:bg-neutral-50"
          >
            <span className="w-3 h-3 rounded-full" style={{ background: col.color }} />
            <span className="text-[13px] font-medium text-neutral-800 flex-1">{col.label}</span>
            <ArrowRight size={12} className="text-neutral-400" />
          </button>
        ))}
      </div>
    </Modal>
  );
}